import Vue from 'vue';
import axios from 'axios';
import router from '../router';
import i18n from '../plugins/i18n';

// 如果訊息在 i18n 中存在對應的 key，則使用翻譯，否則返回原始訊息
const translateMessage = (statusCode, message) => {
  if (!message) return message;

  // 檢查訊息是否為 i18n key（通常包含點號，如 error.message.xxx）

  const translatedKey = `API.${statusCode}`;
  const translated = i18n.t(translatedKey, { errorCode: statusCode });
  // 如果翻譯結果與原始 key 不同，表示找到了翻譯
  if (translated !== statusCode) {
    return translated;
  }

  // 嘗試使用通用的操作失敗訊息
  const genericKey = 'API.operationFailed';
  const genericTranslated = i18n.t('API.operationFailed', { errorCode: statusCode });
  if (genericTranslated !== genericKey) {
    return genericTranslated;
  }

  // 返回原始訊息（API 返回的內容）
  return message;
};

// const loader = Vue.$loading.show();
// let loaderCount = 0;
const baseAxios = axios.create({
  baseURL: `${process.env.VUE_APP_APIPATH}`,
  showLoading: true,
  requestCount: 0,
  timeoutId: 0,
});

// 添加请求拦截器
baseAxios.interceptors.request.use(
  (config) => {
    const token = document.cookie.replace(/(?:(?:^|.*;\s*)token\s*=\s*([^;]*).*$)|^.*$/, '$1');
    const setConfig = config;
    clearTimeout(baseAxios.defaults.timeoutId);
    if (setConfig.showLoading) Vue.prototype.$bus.$emit('setLoading', true);
    baseAxios.defaults.showLoading = true;
    baseAxios.defaults.requestCount += 1;
    if (token) {
      setConfig.headers.Authorization = `Bearer ${token}`;
    }
    // 延遲送出請求
    return new Promise((resolve) => setTimeout(() => {
      resolve(config);
    }, 600));

    // return config;
  },
  (error) => Promise.reject(error),
);

const httpCode = {
  460: () => {
    const temp = new Date();
    const d = new Date(temp.setDate(temp.getDate() - 1));
    document.cookie = `token=;expires=${d}; path=/`;
    document.cookie = `CloudFront-Policy=; expires=${d}; path=/`;
    document.cookie = `CloudFront-Key-Pair-Id=; expires=${d}; path=/`;
    document.cookie = `CloudFront-Signature=; expires=${d}; path=/`;
    Vue.prototype.$setUserAccount();
    router.push('/error460').catch(() => {});
  },

  401: (msg) => {
    let i18nCode = 40101;
    switch (msg) {
      case '您的帳號已在其他裝置上成功登入。':
        i18nCode = 40101;
        break;
      case '閒置過久，為確保您帳戶安全，系統已將您登出！':
        i18nCode = 40102;
        break;
      default:
        i18nCode = 40103;
        break;
    }

    Vue.prototype.$bus.$emit('pushMessage', 'error', translateMessage(i18nCode, msg));
  },
  402: (msg) => {
    const temp = new Date();
    const d = new Date(temp.setDate(temp.getDate() - 1));
    document.cookie = `token=;expires=${d}; path=/`;
    document.cookie = `CloudFront-Policy=; expires=${d}; path=/`;
    document.cookie = `CloudFront-Key-Pair-Id=; expires=${d}; path=/`;
    document.cookie = `CloudFront-Signature=; expires=${d}; path=/`;
    let i18nCode = 40201;
    switch (msg) {
      case '您目前網路不穩，請重新登入。(3)':
        i18nCode = 40201;
        break;
      case '您目前網路不穩，請重新登入。(4)':
        i18nCode = 40202;
        break;
      default:
        i18nCode = 40201;
        break;
    }
    Vue.prototype.$bus.$emit('pushMessage', 'error', translateMessage(i18nCode, msg));
    Vue.prototype.$setUserAccount();
    router.push('/login').catch(() => {});
  },
  403: (msg) => {
    Vue.prototype.$bus.$emit('pushMessage', 'error', translateMessage(403, msg));
  },
};

// http 攔截器
baseAxios.interceptors.response.use(
  (response) => {
    if (response.config.url !== '/FrontEnd/Captcha/GetCaptchaBase64') {
      if (response.data.statusCode !== 0) {
        Vue.prototype.$bus.$emit('pushMessage', 'error', translateMessage(response.data.statusCode, response.data.message));
      }
    }
    // 每次請求都會去取得會員訊息筆數，每三十秒可以觸發一次
    // 但如果是在會員訊息頁面則強制取得
    if (response.config.url !== '/FrontEnd/Login/Logout') {
      if (
        response.config.url !== 'FrontEnd/Members/MemberMessageUnReadCount'
        && response.config.url !== 'FrontEnd/Members/ModifyMemberPhone'
        && response.config.url !== 'FrontEnd/Members/ModifyMemberMail'
      ) {
        const Force = response.config.url === 'FrontEnd/Members/ReadAllMemberMessage'
          || response.config.url === 'FrontEnd/Members/MemberMessageById'
          || response.config.url === 'FrontEnd/Members/MemberMessage';
        Vue.prototype.$bus.$emit('getUnReadCount', Force);
      }
    }

    baseAxios.defaults.requestCount -= 1;
    if (baseAxios.defaults.requestCount <= 0) {
      baseAxios.defaults.timeoutId = setTimeout(() => {
        Vue.prototype.$bus.$emit('setLoading', false);
      }, 100);
    }

    return response;
  },
  (error) => {
    baseAxios.defaults.requestCount -= 1;
    if (baseAxios.defaults.requestCount <= 0) {
      baseAxios.defaults.timeoutId = setTimeout(() => {
        Vue.prototype.$bus.$emit('setLoading', false);
      }, 100);
    }

    const errorCode = Object.keys(httpCode).find((x) => x === error.response.status.toString());

    if (error && error.response && errorCode !== undefined) {
      const token = document.cookie.replace(/(?:(?:^|.*;\s*)token\s*=\s*([^;]*).*$)|^.*$/, '$1');
      if ((token || Vue.prototype.$UserAccount) && error.response.status === 401) {
        const temp = new Date();
        const d = new Date(temp.setDate(temp.getDate() - 1));
        document.cookie = `token=;expires=${d}; path=/`;
        document.cookie = `CloudFront-Policy=; expires=${d}; path=/`;
        document.cookie = `CloudFront-Key-Pair-Id=; expires=${d}; path=/`;
        document.cookie = `CloudFront-Signature=; expires=${d}; path=/`;
        Vue.prototype.$setUserAccount();
        httpCode[error.response.status](error.response.data.error);

        router.push('/login').catch(() => {});
        return Promise.resolve({ data: { statusCode: 99999 } });
      }
      if (error.response.status !== 401) {
        httpCode[error.response.status](error.response.data.error);
      }
    } else {
      Vue.prototype.$bus.$emit('pushMessage', 'error', i18n.t('error.message.operationFailed'));
      return Promise.resolve({ data: { statusCode: 99999 } });
    }

    return Promise.reject(error);
  },
);

export default baseAxios;
